import { inject, Injectable } from '@angular/core';
import { forkJoin, Observable, of, Subscriber } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as qz from 'qz-tray';

import { FormatService } from './format.service';
import { HttpService } from './http.service';
import { PrintNodeService } from './print-node.service';
import { UtilityService } from './utility.service';
import { PrintHtml, PrintTemplate, ShipperResponse } from '../models';
import { UserPrinterDocument } from '../../user-admin';
import { DocumentSize, FileTypes } from '../constants';

@Injectable({
  providedIn: 'root',
})
export class PrintService {
  private readonly httpService = inject(HttpService);
  private readonly formatService = inject(FormatService);
  private readonly printNodeService = inject(PrintNodeService);
  private readonly utilityService = inject(UtilityService);

  // QZ Tray allows one websocket connection per page. Independent print flows (e.g. the receipt
  // labels batch and the picking-list/sample label prints fired by the same save) used to race
  // connect() and QZ threw "An open connection with QZ Tray already exists". Every QZ job is
  // chained onto this queue so each connect -> print -> disconnect cycle finishes before the next
  // starts. The chain always continues (failures resolve, never reject).
  private qzQueue: Promise<void> = Promise.resolve();

  private enqueueQzJob(job: () => Promise<void>): void {
    this.qzQueue = this.qzQueue.then(job, job);
  }

  private static connectQz(): Promise<void> {
    // Reuse an already-open connection rather than racing a second connect.
    return qz.websocket.isActive() ? Promise.resolve() : qz.websocket.connect();
  }

  private static disconnectQz(): Promise<void> {
    return qz.websocket.disconnect().catch(() => undefined);
  }

  printTemplates(prints: PrintTemplate[]): Observable<any> {
    return this.utilityService.arrayExecuteAsyncSequentially(prints, (print: PrintTemplate) =>
      this.printTemplate(print.template, print.model, print.printer, print.copies),
    );
  }

  printHtmls(prints: PrintHtml[], reverse = false): Observable<any> {
    return this.utilityService.arrayExecuteAsyncSequentially(reverse ? prints.reverse() : prints, (print: PrintHtml) =>
      this.printHtml(print.html, print.printer, print.copies),
    );
  }

  printTemplate(template: string, model: any, printer: UserPrinterDocument, copies = 1): Observable<any> {
    if (printer.printMethod === 'PrintNode' && printer.printNodeId) {
      return this.buildHtmlFromTemplate(
        printer.documentSize === DocumentSize.small ? template : `${template}-medium`,
        model,
        printer.printBarcode,
        printer.printQRCode,
        printer.documentSize,
      ).pipe(
        switchMap((html: string) => this.printNodeService.printHtml(printer.printNodeId, html, 'Print Job', copies)),
      );
    }
    return this.printFromTemplate(
      printer.documentSize === DocumentSize.small ? template : `${template}-medium`,
      model,
      printer.printerAddress,
      printer.documentWidth > 0 ? printer.documentWidth : undefined,
      printer.documentHeight > 0 ? printer.documentHeight : undefined,
      printer.documentOrientation,
      printer.topMargin,
      printer.rightMargin,
      printer.bottomMargin,
      printer.leftMargin,
      printer.documentUnits?.toLowerCase(),
      printer.printBarcode,
      printer.printQRCode,
      printer.documentSize,
      copies,
    );
  }

  printHtml(html: string, printer: UserPrinterDocument, copies = 1): Observable<any> {
    if (printer.printMethod === 'PrintNode' && printer.printNodeId) {
      return this.printNodeService.printHtml(printer.printNodeId, html, 'Print Job', copies);
    }
    return this.printFromHtml(
      html,
      printer.printerAddress,
      printer.documentWidth > 0 ? printer.documentWidth : undefined,
      printer.documentHeight > 0 ? printer.documentHeight : undefined,
      printer.documentOrientation,
      printer.topMargin,
      printer.rightMargin,
      printer.bottomMargin,
      printer.leftMargin,
      printer.documentUnits?.toLowerCase(),
      copies,
    );
  }

  printShippingLabels(response: ShipperResponse): Observable<any> {
    if (!response?.label) {
      return of(null);
    }
    return this.resolveShippingPrinter(response.labelDocumentDescription).pipe(
      switchMap((printer: UserPrinterDocument) => {
        if (!printer) {
          return of(null);
        }
        const labels = response.label.split(',');
        return this.utilityService.arrayExecuteAsyncSequentially(labels, (label: string) =>
          this.printShippingDocument(label, response.labelContentType, printer),
        );
      }),
    );
  }

  printShippingInvoices(response: ShipperResponse): Observable<any> {
    if (!response?.invoice) {
      return of(null);
    }
    return this.resolveShippingPrinter(response.labelDocumentDescription).pipe(
      switchMap((printer: UserPrinterDocument) => {
        if (!printer) {
          return of(null);
        }
        const invoices = response.invoice.split(',');
        return this.utilityService.arrayExecuteAsyncSequentially(invoices, (invoice: string) =>
          this.printShippingDocument(invoice, response.invoiceContentType, printer),
        );
      }),
    );
  }

  private resolveShippingPrinter(documentDescription: string): Observable<UserPrinterDocument> {
    if (!documentDescription) {
      return of(null);
    }
    return this.httpService
      .get<UserPrinterDocument>(
        `TmsWeb/UserPrinterDocument/GetForDocument?documentDescription=${encodeURIComponent(documentDescription)}`,
      )
      .pipe(catchError(() => of(null)));
  }

  private printShippingDocument(
    base64Content: string,
    contentType: string,
    printer: UserPrinterDocument,
  ): Observable<any> {
    if (!base64Content || !printer) {
      return of(null);
    }
    if (printer.printMethod === 'PrintNode' && printer.printNodeId) {
      const title = printer.documentDescription || 'Shipping Label';
      return this.printNodeService.printPdf(printer.printNodeId, base64Content, title, 1).pipe(map(() => null));
    }
    return this.printFromShippingLabel(base64Content, contentType, printer.printerAddress);
  }

  getPageModel(model: any, index: number = undefined, pages: any[] = undefined): string {
    return {
      ...model,
      page: `${index + 1} ${$localize`of`} ${pages.length}`,
    };
  }

  getPages<T>(items: T[], itemsPerPage = 8, itemsPerFooter = 3) {
    const pages = this.utilityService.chunk(items, itemsPerPage);

    // An empty list still needs one page so the header/footer render (e.g. a sample-only picking
    // list whose lines are all 'to follow' in the footer). Without this, pages[-1] would throw.
    if (pages.length === 0) {
      pages.push([]);
      return pages;
    }

    // if last page doesn't have enough gap for a footer, push footer onto a new page
    if (pages[pages.length - 1].length + itemsPerFooter > itemsPerPage) {
      pages.push([]);
    }

    return pages;
  }

  replaceTags(html: string, model: any): string {
    const tags = html.match(/<!--(.*?)-->/g);
    if (tags) {
      for (const tag of tags) {
        const prop = tag.replace('<!--', '').replace('-->', '');

        const regex = new RegExp(/(\w*)\((.*?)\)/g);
        const match = regex.exec(prop);
        if (match) {
          const func = match[1];
          const paramsArray = match[2].startsWith('[') && match[2].endsWith(']');
          if (paramsArray) {
            match[2] = match[2].substring(1, match[2].length - 1);
          }
          const params = match[2].split(',').map((x) => x.trim());
          switch (func) {
            case 'show':
              // shows if one or more params properties are truthy (not empty or 0)
              // example usage: <!--show(param)--> or <!--show(param1, param2)--> or <!--show([param1, param2])--> (recommend without [ ] but either will work)
              html = html.replace(
                tag,
                params.some((param) => this.utilityService.getNestedProperty(model, param)) ? '' : 'd-none',
              );
              break;
            case 'showArray':
              // shows if any object in an array has one or more params properties are truthy (not empty or 0) - useful for hiding table th headers
              // example usage: <!--showArray(param)--> or <!--showArray(param1, param2)--> or <!--showArray([param1, param2])--> (recommend without [ ] but either will work)
              html = html.replace(
                tag,
                model.some((modelItem: any) =>
                  params.some((param) => this.utilityService.getNestedProperty(modelItem, param)),
                )
                  ? ''
                  : 'd-none',
              );
              break;
            case 'hide':
              // hides if one or more params properties are truthy (not empty or 0)
              // example usage: <!--hide(param)--> or <!--hide(param1, param2)--> or <!--hide([param1, param2])--> (recommend without [ ] but either will work)
              html = html.replace(
                tag,
                params.some((param) => this.utilityService.getNestedProperty(model, param)) ? 'd-none' : '',
              );
              break;
            case 'hideArray':
              // hides if any object in an array has one or more params properties are truthy (not empty or 0) - useful for hiding table th headers
              // example usage: <!--hideArray(param)--> or <!--hideArray(param1, param2)--> or <!--hideArray([param1, param2])--> (recommend without [ ] but either will work)
              html = html.replace(
                tag,
                model.some((modelItem: any) =>
                  params.some((param) => this.utilityService.getNestedProperty(modelItem, param)),
                )
                  ? 'd-none'
                  : '',
              );
              break;
            default:
              if (paramsArray) {
                // dynamic formatService.func named function with a single array param - e.g. formatService.valuesToHtml([...])
                // example usage: <!--valuesToHtml([param1, param2])-->
                const modelParamArray: any[] = params.map((param) => model[param] ?? '').filter((param) => param);
                html = html.replace(tag, this.formatService[func]?.bind(this.formatService, modelParamArray)() ?? '');
              } else {
                // dynamic formatService.func named function with any number of params - e.g. formatService.number(...)
                // example usage: <!--number(value)--> or <!--currency(value, currencySymbol)-->
                const modelParams: any[] = params.map((param) => model[param] ?? param);
                html = html.replace(tag, this.formatService[func]?.bind(this.formatService, ...modelParams)() ?? '');
              }
              break;
          }
        } else {
          html = html.replace(tag, this.utilityService.getNestedProperty(model, prop) ?? '');
        }
      }
    }
    return html;
  }

  private buildHtmlFromTemplate(
    template: string,
    model: any,
    printBarcode = false,
    printQRCode = false,
    documentSize: string = DocumentSize.small,
  ): Observable<string> {
    return forkJoin([
      this.httpService.getHtmlTemplate('templates/base-template.html'),
      this.httpService.getHtmlTemplate(`templates/${template}.html`),
      this.httpService.getHtmlTemplate(
        documentSize === DocumentSize.small ? 'templates/barcode-label.html' : 'templates/barcode-label-medium.html',
      ),
      this.httpService.getHtmlTemplate(
        documentSize === DocumentSize.small ? 'templates/qrcode-label.html' : 'templates/qrcode-label-medium.html',
      ),
    ]).pipe(
      map(([base, tmpl, barcode, qrcode]) => {
        tmpl = this.replaceTags(tmpl, model);
        tmpl = this.replaceTags(base, { body: tmpl });
        if (printBarcode && model.barcode?.length < 15) {
          tmpl += this.replaceTags(barcode, model);
        }
        if (printQRCode || model.barcode?.length > 14) {
          tmpl += this.replaceTags(qrcode, model);
        }
        return tmpl;
      }),
    );
  }

  private printFromTemplate(
    template: string,
    model: any,
    printer?: string,
    width = 6,
    height = 4,
    orientation = 'portrait',
    top = 0,
    right = 0,
    bottom = 0,
    left = 0,
    units = 'in',
    printBarcode = false,
    printQRCode = false,
    documentSize: string = DocumentSize.small,
    copies = 1,
  ): Observable<any> {
    return this.buildHtmlFromTemplate(template, model, printBarcode, printQRCode, documentSize).pipe(
      switchMap((html: string) =>
        this.printFromHtml(html, printer, width, height, orientation, top, right, bottom, left, units, copies),
      ),
    );
  }

  private printFromHtml(
    html: string,
    printer?: string,
    width = 6,
    height = 4,
    orientation = 'portrait',
    top = 0,
    right = 0,
    bottom = 0,
    left = 0,
    units = 'in',
    copies = 1,
  ): Observable<any> {
    return new Observable((observer: Subscriber<void>) => {
      this.enqueueQzJob(
        () =>
          new Promise<void>((done) => {
            PrintService.connectQz()
              .then(() => qz.printers.getDefault())
              .then((defaultPrinter: string) => {
                const config = qz.configs.create(printer ?? defaultPrinter, {
                  copies: copies,
                  units: units,
                  size: {
                    width,
                    height,
                  },
                  orientation: orientation,
                  margins: { top, right, bottom, left },
                  scaleContent: false,
                  //legacy: true,
                  // legacy: false,
                  // rasterize: true,
                  // colorType: 'blackwhite',
                  // interpolation: 'nearest',
                  // bounds: { x: 0, y: 0, width, height },
                });
                const data = [
                  {
                    type: 'pixel',
                    format: 'html',
                    flavor: 'plain',
                    data: html,
                    // options: {
                    //   alpha: false,
                    //   background: '#FFFFFF',
                    // },
                  },
                ];
                qz.print(config, data).then(
                  () => {
                    PrintService.disconnectQz().then(() => {
                      observer.next();
                      observer.complete();
                      done();
                    });
                  },
                  () => {
                    PrintService.disconnectQz().then(() => {
                      observer.complete();
                      done();
                    });
                  },
                );
              })
              .catch((e: any) => {
                alert(e);
                PrintService.disconnectQz().then(() => {
                  observer.error(e);
                  observer.complete();
                  done();
                });
              });
          }),
      );
    });
  }

  private printFromShippingLabel(label: string, contentType: string, printerAddress: string) {
    return new Observable((observer: Subscriber<void>) => {
      this.enqueueQzJob(
        () =>
          new Promise<void>((done) => {
            PrintService.connectQz()
              .then(() => (printerAddress ? Promise.resolve(printerAddress) : qz.printers.getDefault()))
              .then((printerName: string) => {
                const config = qz.configs.create(printerName);
                const data = [
                  {
                    type: 'pixel',
                    format: contentType === FileTypes.pdf ? 'pdf' : 'image',
                    flavor: 'base64',
                    data: label,
                  },
                ];
                qz.print(config, data).then(
                  () =>
                    PrintService.disconnectQz().then(() => {
                      observer.next();
                      observer.complete();
                      done();
                    }),
                  () =>
                    PrintService.disconnectQz().then(() => {
                      observer.complete();
                      done();
                    }),
                );
              })
              .catch((e: any) => {
                alert(e);
                PrintService.disconnectQz().then(() => {
                  observer.error(e);
                  observer.complete();
                  done();
                });
              });
          }),
      );
    });
  }
}
